import { useEffect, useRef, useState } from "react";
import {
  ArrowDown,
  ArrowLeft,
  Bell,
  BellOff,
  ChevronsUp,
  Copy,
  Eraser,
  ImageUp,
  Lock,
  MessageSquareText,
  MoreHorizontal,
  Paintbrush2,
  Phone,
  PhoneCall,
  RotateCw,
  Search,
  Shield,
  UserCheck,
  UserMinus,
  Video
} from "lucide-react";
import Avatar from "../common/Avatar";
import { formatDayLabel, formatTime } from "../../utils/time";

function lastSeenText(partner, nowMs = Date.now()) {
  if (!partner?.last_seen) return "offline";
  const lastSeenTime = new Date(partner.last_seen).getTime();
  const diffMs = nowMs - lastSeenTime;
  const diffSecs = Math.floor(diffMs / 1000);

  if (diffSecs < 0) return "online"; // clock skew safety
  if (diffSecs < 60) {
    if (diffSecs <= 5) return "last seen just now";
    return `last seen ${diffSecs} seconds ago`;
  }

  const diffMins = Math.floor(diffSecs / 60);
  if (diffMins < 60) {
    return `last seen ${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
  }

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) {
    return `last seen ${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
  }

  const day = formatDayLabel(partner.last_seen);
  const time = formatTime(partner.last_seen);
  if (!day || !time) return "offline";
  if (day === "Today") return `last seen today at ${time}`;
  if (day === "Yesterday") return `last seen yesterday at ${time}`;
  return `last seen ${day}, ${time}`;
}

function resolveDisplayName(partner, isGroup) {
  if (isGroup) return partner?.name || "Unnamed group";
  const rawName = (partner?.name || "").toString().trim();
  const numericName = rawName && /^\d+$/.test(rawName);
  const emailAlias = (partner?.email || "").toString().trim().split("@")[0] || "";
  if (!rawName) return emailAlias || "Unknown";
  if (numericName && emailAlias) return emailAlias;
  return rawName;
}

export default function ChatHeader({
  chat,
  partner,
  typing = false,
  typingName = "",
  mobile,
  showOnlineStatus = true,
  isGroup = false,
  memberCount = 0,
  onBack,
  onVoiceCall,
  onVideoCall,
  onVideoChat,
  onToggleWatchTogether,
  watchTogetherOpen = false,
  watchTogetherEnabled = true,
  hasActiveWatchSession = false,
  onSearchInChat,
  onOpenProfile,
  onRefreshMessages,
  onOpenCallLogs,
  onGoToFirstMessage,
  onGoToLatestMessage,
  onOpenAdminPortal,
  onToggleMute,
  onOpenBackgroundPicker,
  onClearChatBackground,
  hasChatBackground = false,
  backgroundPickerOpen = false,
  onHideChat,
  canHideChat = false,
  muted,
  blockedByMe = false,
  blockedMe = false,
  blockActionBusy = false,
  onBlockUser,
  onUnblockUser,
  notify
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!menuOpen) return undefined;
    function closeMenu(event) {
      if (!menuRef.current?.contains(event.target)) setMenuOpen(false);
    }
    window.addEventListener("mousedown", closeMenu);
    return () => window.removeEventListener("mousedown", closeMenu);
  }, [menuOpen]);

  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), 5000);
    return () => clearInterval(timer);
  }, []);

  if (!chat) return null;

  const statusUpdatedAtMs = (() => {
    const raw = partner?.status_updated_at;
    if (typeof raw === "number" && Number.isFinite(raw)) return raw;
    if (typeof raw === "string" && raw) {
      const parsed = new Date(raw).getTime();
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
  })();
  const onlineFresh = statusUpdatedAtMs ? (nowMs - statusUpdatedAtMs < 2 * 60 * 1000) : false;
  const online = !isGroup && partner?.status === "online" && onlineFresh;
  const isTyping = Boolean(typing);
  const blocked = !isGroup && (blockedByMe || blockedMe);
  const watchDisabled = !watchTogetherEnabled;
  const displayName = resolveDisplayName(partner, isGroup);
  const aboutText = (partner?.about || "").toString().trim();
  const safeTypingName = (typingName || "").toString().trim() || "Someone";
  const typingSubtitle = isTyping ? (isGroup ? `${safeTypingName} typing...` : "typing...") : "";
  const subtitleText = typingSubtitle || (isGroup
    ? (memberCount ? `${memberCount} members` : "Group chat")
    : (chat.chat_type === "self"
      ? "Only visible to you"
      : (showOnlineStatus ? (online ? "online" : lastSeenText(partner, nowMs)) : "")));
  const contactText = isGroup
    ? (memberCount ? `${memberCount} members` : "Group chat")
    : (chat?.other_user_mobile || chat?.other_user_email || "No contact info");
  const subtitleClass = isTyping
    ? "text-violet-600 dark:text-violet-300"
    : (online ? "text-emerald-500" : "text-slate-500 dark:text-slate-400");

  async function copyContact() {
    try {
      await navigator.clipboard.writeText(contactText);
      notify?.({ type: "success", message: "Contact copied to clipboard." });
    } catch {
      notify?.({ type: "error", message: "Unable to copy contact." });
    } finally {
      setMenuOpen(false);
    }
  }

  return (
    <header className="sticky top-0 z-30 glass-bar rounded-none border-x-0 border-t-0 border-b border-slate-200/60 px-3 py-2.5 shadow-[0_10px_30px_rgba(0,0,0,0.10)] dark:border-white/10 sm:px-5 sm:py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex items-center gap-3">
          {mobile && (
            <button
              type="button"
              onClick={onBack}
              className="glass-icon-btn disabled:cursor-not-allowed disabled:opacity-45"
              aria-label="Back to chats"
            >
              <ArrowLeft size={18} />
            </button>
          )}
          <button
            type="button"
            onClick={onOpenProfile}
            className="min-w-0 rounded-2xl border border-transparent px-1.5 py-1 text-left transition hover:border-white/10 hover:bg-white/25 focus-visible:outline-none focus-visible:ring-2 ring-accent dark:hover:bg-white/5"
            aria-label="Open contact profile"
            title="Open profile"
          >
            <span className="flex min-w-0 items-center gap-3">
              <Avatar name={partner?.name} src={partner?.avatar_url} size={42} />
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{displayName}</span>
                {subtitleText ? (
                  <span className={`block truncate text-xs ${subtitleClass}`}>
                    {subtitleText}
                  </span>
                ) : null}
              </span>
            </span>
          </button>
        </div>

        <div className="flex items-center gap-1">
          <HeaderAction label="Search in chat" onClick={onSearchInChat}>
            <Search size={16} />
          </HeaderAction>
          {/* Watch Together Action Disabled */}
          {!isGroup && (
            <>
              <HeaderAction label="Voice call" onClick={onVoiceCall} disabled={blocked} className="hidden md:inline-flex">
                <Phone size={16} />
              </HeaderAction>
              <HeaderAction label="Video call" onClick={onVideoCall} disabled={blocked} className="hidden md:inline-flex">
                <Video size={16} />
              </HeaderAction>
              <HeaderAction
                label="Video chat (mic off)"
                onClick={onVideoChat}
                className="hidden md:inline-flex"
                disabled={blocked}
              >
                <MessageSquareText size={16} />
              </HeaderAction>
            </>
          )}
          <div className="relative" ref={menuRef}>
            <HeaderAction label="More actions" onClick={() => setMenuOpen((prev) => !prev)}>
              <MoreHorizontal size={16} />
            </HeaderAction>

             {menuOpen && (
              <div className="absolute right-0 top-12 z-20 w-72 rounded-2xl border border-slate-200/80 bg-white/95 p-3 shadow-[0_20px_50px_rgba(0,0,0,0.15)] backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/95 max-h-[80vh] overflow-y-auto no-scrollbar">
                <div className="mb-3 rounded-2xl border border-violet-100 bg-gradient-to-br from-violet-50/50 to-pink-50/30 p-4 dark:border-violet-950/40 dark:from-violet-950/20 dark:to-pink-950/10">
                  <p className="text-[9px] font-bold uppercase tracking-wider text-violet-500 dark:text-violet-400 mb-1.5">Chat details</p>
                  <p className="truncate text-base font-bold text-slate-900 dark:text-slate-100 leading-tight">{displayName}</p>
                  {!isGroup && aboutText && (
                    <p className="mt-1 truncate text-xs font-medium text-slate-600 dark:text-slate-300 italic">“{aboutText}”</p>
                  )}
                  <p className="mt-2 truncate text-xs font-semibold text-slate-500 dark:text-slate-400 font-mono tracking-tight bg-white/60 dark:bg-black/20 px-2 py-1 rounded-lg border border-black/5 dark:border-white/5 inline-block">{contactText}</p>
                  {isGroup && (
                    <p className="mt-1.5 text-xs font-medium text-slate-600 dark:text-slate-400">
                      👥 {memberCount ? `${memberCount} members` : "Group chat"}
                    </p>
                  )}
                  {showOnlineStatus && (
                    <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">{online ? "Online now" : lastSeenText(partner, nowMs)}</p>
                  )}
                  {!isGroup && blockedByMe && (
                    <p className="mt-1 rounded-md border border-amber-300/70 bg-amber-50 px-1.5 py-1 text-[11px] text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
                      You blocked this user.
                    </p>
                  )}
                  {!isGroup && !blockedByMe && blockedMe && (
                    <p className="mt-1 rounded-md border border-rose-300/70 bg-rose-50 px-1.5 py-1 text-[11px] text-rose-800 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-200">
                      This user blocked you.
                    </p>
                  )}
                </div>

                <MenuButton
                  icon={<Search size={14} />}
                  label="Search messages"
                  onClick={() => {
                    onSearchInChat?.();
                    setMenuOpen(false);
                  }}
                />
                {/* Watch Together Dropdown Button Disabled */}
                {!isGroup && (
                  <>
                    <MenuButton
                      icon={<Phone size={14} />}
                      label="Voice call"
                      disabled={blocked}
                      onClick={() => {
                        onVoiceCall?.();
                        setMenuOpen(false);
                      }}
                    />
                    <MenuButton
                      icon={<Video size={14} />}
                      label="Video call"
                      disabled={blocked}
                      onClick={() => {
                        onVideoCall?.();
                        setMenuOpen(false);
                      }}
                    />
                    <MenuButton
                      icon={<MessageSquareText size={14} />}
                      label="Video chat (mic off)"
                      disabled={blocked}
                      onClick={() => {
                        onVideoChat?.();
                        setMenuOpen(false);
                      }}
                    />
                  </>
                )}
                {!isGroup && blockedByMe && (
                  <MenuButton
                    icon={<UserCheck size={14} />}
                    label={blockActionBusy ? "Unblocking..." : "Unblock user"}
                    disabled={blockActionBusy}
                    onClick={async () => {
                      await onUnblockUser?.();
                      setMenuOpen(false);
                    }}
                  />
                )}
                {!isGroup && !blockedByMe && (
                  <MenuButton
                    icon={<UserMinus size={14} />}
                    label={blockActionBusy ? "Blocking..." : "Block user"}
                    disabled={blockActionBusy || blockedMe}
                    danger
                    onClick={async () => {
                      await onBlockUser?.();
                      setMenuOpen(false);
                    }}
                  />
                )}
                {canHideChat && (
                  <MenuButton
                    icon={<Lock size={14} />}
                    label="Hide chat with PIN"
                    onClick={async () => {
                      await onHideChat?.();
                      setMenuOpen(false);
                    }}
                  />
                )}
                <MenuButton
                  icon={muted ? <Bell size={14} /> : <BellOff size={14} />}
                  label={muted ? "Unmute notifications" : "Mute notifications"}
                  onClick={() => {
                    onToggleMute?.();
                    setMenuOpen(false);
                  }}
                />
                <MenuButton icon={<Copy size={14} />} label="Copy contact" onClick={copyContact} />
                {onOpenCallLogs && (
                  <MenuButton
                    icon={<PhoneCall size={14} />}
                    label="Call logs"
                    onClick={() => {
                      onOpenCallLogs?.();
                      setMenuOpen(false);
                    }}
                  />
                )}
                <MenuButton
                  icon={<RotateCw size={14} />}
                  label="Refresh messages"
                  onClick={() => {
                    onRefreshMessages?.();
                    setMenuOpen(false);
                  }}
                />
                {onGoToFirstMessage && (
                  <MenuButton
                    icon={<ChevronsUp size={14} />}
                    label="Go to first message"
                    onClick={() => {
                      onGoToFirstMessage?.();
                      setMenuOpen(false);
                    }}
                  />
                )}
                {onGoToLatestMessage && (
                  <MenuButton
                    icon={<ArrowDown size={14} />}
                    label="Go to latest message"
                    onClick={() => {
                      onGoToLatestMessage?.();
                      setMenuOpen(false);
                    }}
                  />
                )}
                {onOpenAdminPortal && (
                  <MenuButton
                    icon={<Shield size={14} />}
                    label="Go to Admin Panel"
                    onClick={() => {
                      onOpenAdminPortal?.();
                      setMenuOpen(false);
                    }}
                  />
                )}
                <MenuButton
                  icon={backgroundPickerOpen ? <ImageUp size={14} /> : <Paintbrush2 size={14} />}
                  label={backgroundPickerOpen ? "Hide background options" : "Change chat background"}
                  onClick={() => {
                    onOpenBackgroundPicker?.();
                    setMenuOpen(false);
                  }}
                />
                {hasChatBackground && (
                  <MenuButton
                    icon={<Eraser size={14} />}
                    label="Reset chat background"
                    onClick={() => {
                      onClearChatBackground?.();
                      setMenuOpen(false);
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

function HeaderAction({ children, label, onClick, className = "", disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || !onClick}
      className={`glass-icon-btn disabled:cursor-not-allowed disabled:opacity-45 ${className}`}
      aria-label={label}
      title={label}
    >
      {children}
    </button>
  );
}

function MenuButton({ icon, label, onClick, danger = false, disabled = false }) {
  const iconBaseClass = `flex h-7 w-7 items-center justify-center rounded-lg transition-transform group-hover:scale-115 duration-200 shrink-0 ${
    danger 
      ? "bg-rose-500/10 text-rose-500 dark:bg-rose-500/20 dark:text-rose-400"
      : "bg-violet-500/10 text-violet-500 dark:bg-violet-500/20 dark:text-violet-400"
  }`;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`group flex w-full items-center gap-3 rounded-xl px-2.5 py-1.5 text-left text-xs font-semibold transition-all duration-200 ${
        disabled
          ? "cursor-not-allowed opacity-35"
          : danger
            ? "text-rose-600 hover:bg-rose-50/80 dark:text-rose-300 dark:hover:bg-rose-950/20"
            : "text-slate-700 hover:bg-violet-50/60 dark:text-slate-200 dark:hover:bg-violet-950/15"
      }`}
    >
      <div className={iconBaseClass}>
        {icon}
      </div>
      <span className="truncate">{label}</span>
    </button>
  );
}
